import dayjs, {Dayjs} from 'dayjs';
import {GoalProgress} from './proportions';

export type SuggestedGoalPerformance = 'on_track' | 'at_risk' | 'behind';
export type GoalDisplayPerformance =
  | SuggestedGoalPerformance
  | 'upcoming'
  | 'no_status';
export type GoalAttentionStatus = GoalDisplayPerformance | 'overdue';

const endedGoalStatuses = new Set([
  'ended',
  'completed',
  'in_completed',
  'in_complete',
]);

export const goalIsEnded = (goal: any): boolean =>
  endedGoalStatuses.has(String(goal?.status || '').toLowerCase());

export const goalEndedAt = (goal: any): string | undefined =>
  goal?.completedAt || goal?.endedAt || goal?.updatedAt;

export const savedGoalPerformance = (
  goal: any,
): SuggestedGoalPerformance | undefined => {
  const performance = String(goal?.performance || goal?.progress?.type || '')
    .toLowerCase()
    .replace(/[-\s]/g, '_');

  if (performance === 'ontrack') return 'on_track';
  if (
    performance === 'on_track' ||
    performance === 'at_risk' ||
    performance === 'behind'
  ) {
    return performance;
  }

  return undefined;
};

export const goalIsUpcoming = (goal: any, today: Dayjs = dayjs()): boolean => {
  if (!goal?.startDate) return false;
  const startDate = dayjs(goal.startDate);
  return (
    startDate.isValid() &&
    startDate.startOf('day').isAfter(today.startOf('day'))
  );
};

export const goalIsOverdue = (goal: any, today: Dayjs = dayjs()): boolean => {
  if (goalIsEnded(goal)) return false;
  if (String(goal?.status || '').toLowerCase() === 'past_due') return true;
  return Boolean(
    goal?.endDate && dayjs(goal.endDate).endOf('day').isBefore(today),
  );
};

export const goalDisplayPerformance = (
  goal: any,
  today: Dayjs = dayjs(),
): GoalDisplayPerformance =>
  savedGoalPerformance(goal) ||
  (goalIsUpcoming(goal, today) ? 'upcoming' : 'no_status');

// A missed deadline takes precedence in attention-facing lists; the saved
// performance remains unchanged and available in the goal details.
export const goalAttentionStatus = (
  goal: any,
  today: Dayjs = dayjs(),
): GoalAttentionStatus =>
  goalIsOverdue(goal, today) ? 'overdue' : goalDisplayPerformance(goal, today);

const clamp = (value: unknown) =>
  Math.min(100, Math.max(0, Number(value) || 0));

export const suggestedGoalPerformance = (
  goal: any,
  today: Dayjs = dayjs(),
): SuggestedGoalPerformance => {
  const progress = clamp(GoalProgress(goal).percentage);
  const start = dayjs(goal?.startDate);
  const end = dayjs(goal?.endDate);
  const totalDays = Math.max(1, end.diff(start, 'day'));
  const elapsed = clamp((today.diff(start, 'day') / totalDays) * 100);
  const variance = progress - elapsed;

  if (variance >= -10) return 'on_track';
  if (variance >= -25) return 'at_risk';
  return 'behind';
};
